//@ts-check
import { Controller } from "@hotwired/stimulus";
import { mustBeHtmlElement } from "common/dom-utils";
import { getCsrfToken } from "common/utils/rails";

/**
 * Controller to handle the buttons for adding/removing a good from the shopping cart.
 * Calls backend (Store::ShoppingCartItemsController) to save data and updates the DOM based on the response JSON.
 * Buttons defined in app/views/store/goods/_good.html.erb
 */
export default class extends Controller {
  static targets = ["total"];

  connect() {
    console.log("CartAddRemoveGoodButtonController connected", this.element);
    console.log("CartAddRemoveGoodButtonController initialized");
  }

  async add(event) {
    console.log("Add to cart clicked");
    event.preventDefault();
    event.stopPropagation();

    const button = event.currentTarget;

    if (button.classList.contains("disabled")) {
      console.log("Button is disabled, not adding to cart.");
      return;
    }

    const url = findFormTargetUrl(button);

    await this.request(url, { method: "POST" }, { context: { action: "add" } });
  }

  async remove(event) {
    console.log("Remove from cart clicked");
    event.preventDefault();
    event.stopPropagation();

    const button = event.currentTarget;

    const url = findFormTargetUrl(button);

    await this.request(
      url,
      { method: "DELETE" },
      { context: { action: "remove" } }
    );
  }

  // ---- private

  async request(url, options, meta) {
    try {
      const res = await fetch(url, {
        ...options,
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": getCsrfToken(),
        },
        credentials: "same-origin",
      });

      const data = await res.json();

      if (!res.ok) {
        this.handleError(data, meta);
        return;
      }

      this.updateDom(data, meta);
    } catch (e) {
      console.error("Error while updating cart:", e);
      alert("Something went wrong while updating your cart.");
    }
  }

  handleError(data, meta) {
    const message = data?.error || "There was a problem.";
    this.showMessage(message);
  }

  updateDom(payload, meta) {
    // Payload generated in shopping_cart_items#cart_payload
    console.log("Processing response:", payload);
    const cart = payload?.cart;
    if (!cart) {
      console.error("No cart data in response:", payload);
      return;
    }

    this.updateShoppingCartTotal(cart.total_amount_formatted);
    this.updateButtonsAndHighlights(cart);
  }

  updateButtonsAndHighlights(cart) {
    // Structure defined in: Checkout::CartSerializer

    const items = cart.items || [];

    const goodElement = mustBeHtmlElement(this.element.closest(".good"));
    const goodId = goodElement.dataset.goodId;

    // Find item for the current purchasable
    const item = items.find(
      (item) =>
        item.purchasable &&
        item.purchasable.type == "Good" &&
        item.purchasable.id == goodId
    );

    const button = goodElement.querySelector(".good__purchase-box .button");
    if (!button) {
      throw new Error("Button not found in good element");
    }

    if (!item) {
      // This good is not in the cart, change both buttons to "Add to cart"
      this.makeAddable(button);
    } else {
      // This good is in the cart, change both buttons to "Remove from cart"
      this.makeRemovable(button, item);
    }
  }

  updateShoppingCartTotal(totalAmountFormatted) {
    if (totalAmountFormatted === undefined || totalAmountFormatted === null) {
      console.error("Total amount is undefined or null");
      return;
    }

    const totalElement = mustBeHtmlElement(
      document.querySelector(".cart__amount")
    );

    totalElement.innerHTML = totalAmountFormatted;
  }

  makeAddable(button) {
    button.value = "Add to cart";
    button.dataset.controller = "cart-add-remove-good-button";
    button.dataset.action = "click->cart-add-remove-good-button#add";
    button.classList.remove("button--toggled");

    const goodElement = mustBeHtmlElement(this.element.closest(".good"));

    // /shopping_cart_items?id=1&shipping_category=intl&type=good
    const queryString = new URLSearchParams({
      type: "good",
      id: goodElement.dataset.goodId,
    }).toString();

    const form = button.closest("form");
    if (!form) {
      throw new Error("Form not found for button");
    }

    form.setAttribute("action", `/shopping_cart_items.json?${queryString}`);
    form.setAttribute("method", "post");
    const methodElement = form.querySelector('input[name="_method"]');
    if (methodElement) {
      methodElement.value = "post";
    }
  }

  makeRemovable(button, item) {
    button.value = "Remove from cart";
    button.dataset.controller = "cart-add-remove-good-button";
    button.dataset.action = "click->cart-add-remove-good-button#remove";
    button.classList.add("button--toggled");

    const form = button.closest("form");
    if (!form) {
      throw new Error("Form not found for button");
    }

    form.setAttribute("action", `/shopping_cart_items/${item.id}.json`);
    form.setAttribute("method", "post");
    const methodElement = form.querySelector('input[name="_method"]');
    if (methodElement) {
      methodElement.value = "delete";
    }
  }

  showMessage(message) {
    // @ts-ignore
    window.flashMessages.showMessage(message);
  }
}

// ---------- Utilities ----------

function findFormTargetUrl(button) {
  const form = button.closest("form");
  if (!form) {
    throw new Error("Form not found for button");
  }

  const url = form.getAttribute("action");
  if (!url) {
    throw new Error("Form action not found");
  }

  return url;
}
